package com.elan.edubridge.shared.domain.model

import kotlinx.serialization.Serializable

/**
 * 일정(캘린더) 계약. `functions/src/events.ts`의 콜러블 6종에 대응한다.
 *
 * **두 캘린더가 한 모델을 쓴다.** 학원 일정은 `edubridge_academies/{academyId}/events`,
 * 개인 일정은 `edubridge_users/{uid}/events`에 있고 서버에서도 문서 모양이 같다. 화면은 두 목록을
 * 합쳐 한 달치를 하나의 정렬된 리스트로 그리므로, 여기서 타입을 나누면 합치는 쪽이 매번 둘을
 * 공통 형태로 다시 접어야 한다. 구분은 [CalendarEvent.scope] 하나로 한다.
 *
 * **학원당 하나다.** 공지·게시판과 같은 이유로 반별 캘린더는 없다. 정기 수업은 일정이 아니라
 * [ClassSchedule]이 담당한다 — 같은 사실에 주인을 둘 두지 않는다.
 *
 * **반복 일정은 없다.** 단발 일정만 표현하며, [CalendarEvent.startAt]/[CalendarEvent.endAt]은
 * "이 한 건"의 시각이라 나중에 반복 규칙이 붙어도 의미가 바뀌지 않는다.
 *
 * **소프트 삭제**: 삭제 콜러블은 문서를 지우지 않고 [ContentStatus.DELETED] 툼스톤으로 바꾸며
 * 제목·설명을 비운다. ACTIVE 필터는 `EduConnectRepositoryImpl` 한 곳에만 있다.
 */

/**
 * 일정이 어느 캘린더의 것인지. 저장 위치(경로)를 그대로 옮긴 값이며 문서에는 이 필드가 없다 —
 * 어느 컬렉션에서 읽었는지 아는 데이터소스가 채운다.
 */
@Serializable
enum class EventScope(val displayName: String) {
    ACADEMY("학원 일정"),
    PERSONAL("내 일정"),
}

/**
 * `events/{id}` 읽기 모델.
 *
 * **[startAt]·[endAt]은 항상 UTC 정규형 ISO-8601이다**(`2026-08-15T10:00:00.000Z`). 서버가
 * 저장 시점에 `toISOString()`으로 다시 직렬화하므로 클라이언트가 `+09:00` 오프셋으로 보내도
 * 저장된 형태는 하나뿐이다 — 기간 조회가 문자열 비교라서 표현이 둘이면 같은 시각이 다른 순서로
 * 정렬된다.
 *
 * **[allDay]는 표시용 플래그일 뿐 두 번째 시각 표현이 아니다.** 종일 일정도 [startAt]/[endAt]에
 * 진짜 순간이 들어 있고(작성한 쪽의 지역 시간대 기준 하루 경계), 이 값은 "시:분을 그리지 말고
 * 날짜만 그려라"는 뜻이다. 서버가 하루 경계를 대신 계산하지 않는 이유는 개인 일정에 쓸 시간대가
 * 서버에 없기 때문이다.
 */
@Serializable
data class CalendarEvent(
    val id: String,
    val scope: EventScope,
    /** [EventScope.ACADEMY]에서만 채워진다. 개인 일정은 학원에 속하지 않는다. */
    val academyId: String? = null,
    val title: String,
    val description: String = "",
    val startAt: String,
    val endAt: String,
    val allDay: Boolean = false,
    val status: ContentStatus = ContentStatus.ACTIVE,
    val createdBy: String? = null,
    /**
     * 작성 시점의 표시 이름 스냅샷(학원 일정만). rules가 남의 `edubridge_users` 문서를 막으므로
     * "누가 등록했는지"는 클라이언트가 알아낼 방법이 없다 — 공지의 `authorName`과 같은 이유다.
     */
    val createdByName: String? = null,
    val createdAt: String = "",
    val updatedAt: String? = null,
)

// ---- 콜러블 입력 ----
//
// 길이·형식 제한은 서버(`events.ts`)가 강제한다. 제목 1~100자, 설명 0~2000자,
// [startAt]/[endAt]은 파싱 가능한 ISO-8601이어야 하고 `endAt >= startAt`이어야 한다
// (같으면 통과한다 — 길이 0인 "시각 표시" 일정을 허용한다).

/** 학원 일정 등록. 호출자는 EDITOR 이상(학원장·선생님)이어야 한다. */
@Serializable
data class CreateAcademyEventRequest(
    val academyId: String,
    val title: String,
    val description: String = "",
    val startAt: String,
    val endAt: String,
    val allDay: Boolean = false,
)

/**
 * 학원 일정 수정. 다섯 항목 중 **적어도 하나**는 null이 아니어야 한다 — 전부 비우면 서버가
 * `INVALID_ARGUMENT`로 거부한다. null인 항목은 전송하지 않는다.
 *
 * [description]에 빈 문자열을 보내면 설명이 지워진다(전송하지 않는 것과 다르다).
 * 한쪽 시각만 보내면 서버가 저장된 반대쪽과 비교해 순서를 확인한다.
 */
@Serializable
data class UpdateAcademyEventRequest(
    val academyId: String,
    val eventId: String,
    val title: String? = null,
    val description: String? = null,
    val startAt: String? = null,
    val endAt: String? = null,
    val allDay: Boolean? = null,
)

/** 개인 일정 등록. 로그인만 되어 있으면 되고 학원 멤버십은 필요 없다. */
@Serializable
data class CreatePersonalEventRequest(
    val title: String,
    val description: String = "",
    val startAt: String,
    val endAt: String,
    val allDay: Boolean = false,
)

/**
 * 개인 일정 수정. [UpdateAcademyEventRequest]와 같은 규칙이다.
 *
 * `academyId`가 없는 것은 누락이 아니다 — 서버가 대상 문서를 **호출자 uid로** 찾으므로 남의
 * `eventId`를 넣으면 권한 실패가 아니라 `NOT_FOUND`가 된다.
 */
@Serializable
data class UpdatePersonalEventRequest(
    val eventId: String,
    val title: String? = null,
    val description: String? = null,
    val startAt: String? = null,
    val endAt: String? = null,
    val allDay: Boolean? = null,
)

// ---- 콜러블 결과 ----
//
// 수정·삭제 4종은 돌려줄 상태가 id뿐이라 공용 `CommandResult`를 쓰고, 새 문서를 만드는 2종만
// 전용 결과형을 갖는다(공지·게시판과 같은 규약).

/** `createAcademyEvent`·`createPersonalEvent` 결과. */
@Serializable
data class CalendarEventResult(
    val event: CalendarEvent? = null,
    val failure: CommandFailure? = null,
    val errorMessage: String? = null,
) {
    val isSuccess: Boolean get() = failure == null && event != null
}
